/**
 * ROC analysis for a continuous test score and a two-class reference standard.
 *
 * - The curve joins one (1 − specificity, sensitivity) point per distinct score, calling a
 *   case positive when score ≥ threshold; tied scores step diagonally, so the trapezoidal
 *   area equals the Mann–Whitney statistic U / (m·n), ties counting one half.
 * - DeLong, DeLong & Clarke-Pearson (1988): the variance of the AUC from the structural
 *   components (placement values) of each class, Var = S₁₀/m + S₀₁/n. The interval is
 *   AUC ± z·SE, held inside [0, 1] as `pROC::ci.auc` does. Placements come from average
 *   ranks, which makes it O(N log N).
 * - Hanley & McNeil (1982): SE² = [A(1−A) + (m−1)(Q₁−A²) + (n−1)(Q₂−A²)] / (m·n) with
 *   Q₁ = A/(2−A) and Q₂ = 2A²/(1+A), which assumes an exponential score model.
 * - The test of AUC = 0.5 is the Mann–Whitney normal approximation with the tie
 *   correction and no continuity correction, as `wilcox.test(correct = FALSE)`.
 * - Optimal cut-offs: Youden's J = sensitivity + specificity − 1 (Youden 1950), or the
 *   point closest to the top-left corner (0, 1). Ties go to the highest threshold.
 */

import { averageRanks, tieGroupSizes } from "@/lib/stats/descriptive";
import { normalQuantile, normalUpper } from "@/lib/stats/distributions";

export interface RocPoint {
  /** Score ≥ threshold is called positive; Infinity for the empty rule. */
  threshold: number;
  tp: number;
  fp: number;
  fn: number;
  tn: number;
  sensitivity: number;
  specificity: number;
  youden: number;
  /** Distance to the perfect corner (0, 1) in the ROC plane. */
  distance: number;
}

export interface AucInterval {
  se: number;
  lower: number;
  upper: number;
}

export interface RocAnalysis {
  positives: number;
  negatives: number;
  auc: number;
  /** Mann–Whitney U for the positive group. */
  u: number;
  points: RocPoint[];
  delong: AucInterval;
  hanleyMcNeil: AucInterval;
  z: number;
  p: number;
  youden: RocPoint;
  topLeft: RocPoint;
  /** True when scores were negated because lower scores mean positive. */
  reversed: boolean;
}

function pointAt(
  threshold: number,
  tp: number,
  fp: number,
  positives: number,
  negatives: number
): RocPoint {
  const sensitivity = tp / positives;
  const specificity = 1 - fp / negatives;
  return {
    threshold,
    tp,
    fp,
    fn: positives - tp,
    tn: negatives - fp,
    sensitivity,
    specificity,
    youden: sensitivity + specificity - 1,
    distance: Math.hypot(1 - sensitivity, 1 - specificity),
  };
}

/** One point per distinct score, from the strictest rule to the most liberal. */
export function rocPoints(
  positive: readonly number[],
  negative: readonly number[]
): RocPoint[] {
  const m = positive.length;
  const n = negative.length;
  const counts = new Map<number, { pos: number; neg: number }>();
  for (const value of positive) {
    const entry = counts.get(value) ?? { pos: 0, neg: 0 };
    entry.pos += 1;
    counts.set(value, entry);
  }
  for (const value of negative) {
    const entry = counts.get(value) ?? { pos: 0, neg: 0 };
    entry.neg += 1;
    counts.set(value, entry);
  }
  const thresholds = [...counts.keys()].sort((a, b) => b - a);
  const points = [pointAt(Number.POSITIVE_INFINITY, 0, 0, m, n)];
  let tp = 0;
  let fp = 0;
  for (const threshold of thresholds) {
    const entry = counts.get(threshold) ?? { pos: 0, neg: 0 };
    tp += entry.pos;
    fp += entry.neg;
    points.push(pointAt(threshold, tp, fp, m, n));
  }
  return points;
}

/** Trapezoidal area under the curve. */
export function trapezoidAuc(points: readonly RocPoint[]): number {
  let area = 0;
  for (let i = 1; i < points.length; i++) {
    const width = points[i - 1].specificity - points[i].specificity;
    area += (width * (points[i].sensitivity + points[i - 1].sensitivity)) / 2;
  }
  return area;
}

/** DeLong's standard error from placement values. */
export function deLongSe(
  positive: readonly number[],
  negative: readonly number[],
  auc: number
): number {
  const m = positive.length;
  const n = negative.length;
  const all = averageRanks([...positive, ...negative]);
  const rankPositive = averageRanks(positive);
  const rankNegative = averageRanks(negative);
  let s10 = 0;
  for (let i = 0; i < m; i++) {
    const placement = (all[i] - rankPositive[i]) / n;
    s10 += (placement - auc) ** 2;
  }
  let s01 = 0;
  for (let j = 0; j < n; j++) {
    const placement = 1 - (all[m + j] - rankNegative[j]) / m;
    s01 += (placement - auc) ** 2;
  }
  return Math.sqrt(s10 / (m - 1) / m + s01 / (n - 1) / n);
}

export function hanleyMcNeilSe(auc: number, m: number, n: number): number {
  const q1 = auc / (2 - auc);
  const q2 = (2 * auc * auc) / (1 + auc);
  const variance =
    (auc * (1 - auc) +
      (m - 1) * (q1 - auc * auc) +
      (n - 1) * (q2 - auc * auc)) /
    (m * n);
  return Math.sqrt(Math.max(0, variance));
}

function interval(auc: number, se: number, z: number): AucInterval {
  return {
    se,
    lower: Math.max(0, auc - z * se),
    upper: Math.min(1, auc + z * se),
  };
}

function best(
  points: readonly RocPoint[],
  better: (candidate: RocPoint, current: RocPoint) => boolean
): RocPoint {
  let current = points[1] ?? points[0];
  for (const candidate of points.slice(1)) {
    if (better(candidate, current)) {
      current = candidate;
    }
  }
  return current;
}

export function rocAnalysis(
  positiveScores: readonly number[],
  negativeScores: readonly number[],
  higherIsPositive = true,
  level = 0.95
): RocAnalysis | null {
  const m = positiveScores.length;
  const n = negativeScores.length;
  if (m < 2 || n < 2) {
    return null;
  }
  const sign = higherIsPositive ? 1 : -1;
  const positive = positiveScores.map((value) => sign * value);
  const negative = negativeScores.map((value) => sign * value);
  const points = rocPoints(positive, negative);
  const auc = trapezoidAuc(points);
  const zCritical = normalQuantile(1 - (1 - level) / 2);
  const u = auc * m * n;
  const total = m + n;
  const tieTerm = tieGroupSizes([...positive, ...negative]).reduce(
    (sum, t) => sum + (t ** 3 - t),
    0
  );
  const variance =
    ((m * n) / 12) * (total + 1 - tieTerm / (total * (total - 1)));
  const z = variance > 0 ? (u - (m * n) / 2) / Math.sqrt(variance) : Number.NaN;
  return {
    positives: m,
    negatives: n,
    auc,
    u,
    points,
    delong: interval(auc, deLongSe(positive, negative, auc), zCritical),
    hanleyMcNeil: interval(auc, hanleyMcNeilSe(auc, m, n), zCritical),
    z,
    p: Number.isFinite(z)
      ? Math.min(1, 2 * normalUpper(Math.abs(z)))
      : Number.NaN,
    youden: best(points, (a, b) => a.youden > b.youden + 1e-12),
    topLeft: best(points, (a, b) => a.distance < b.distance - 1e-12),
    reversed: !higherIsPositive,
  };
}

/** The 2 × 2 counts for an arbitrary cut-off ("score ≥ cut-off is positive"). */
export function countsAt(
  positiveScores: readonly number[],
  negativeScores: readonly number[],
  cutoff: number,
  higherIsPositive = true
): { tp: number; fp: number; fn: number; tn: number } {
  const call = (score: number) =>
    higherIsPositive ? score >= cutoff : score <= cutoff;
  const tp = positiveScores.filter(call).length;
  const fp = negativeScores.filter(call).length;
  return {
    tp,
    fp,
    fn: positiveScores.length - tp,
    tn: negativeScores.length - fp,
  };
}

export type AucBand =
  | "none"
  | "poor"
  | "acceptable"
  | "excellent"
  | "outstanding";

/** Hosmer & Lemeshow (2000): 0.7–0.8 acceptable, 0.8–0.9 excellent, 0.9+ outstanding. */
export function aucBand(auc: number): AucBand {
  if (auc >= 0.9) {
    return "outstanding";
  }
  if (auc >= 0.8) {
    return "excellent";
  }
  if (auc >= 0.7) {
    return "acceptable";
  }
  return auc > 0.5 ? "poor" : "none";
}
